import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import arrowLeftIconTokens from '@amzn/meridian-tokens/base/icon/arrow-left-small';
import ActionButton from '../components/ActionButton';
import Breadcrumb, { BreadcrumbGroup } from '@amzn/meridian/breadcrumb';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import CatalogJobFailure from '../components/CatalogJobFailure';
import CatalogJobPollingStatus from '../components/CatalogJobPollingStatus';
import StatusBadge from '../components/StatusBadge';
import { describeCatalogStatus, useCatalogJob } from '../hooks/useCatalogJob';
import { CatalogJobStatus } from '../api/services';
import { CatalogJobLocationState, SubmittedCatalogJob, catalogJobHref } from '../utils/catalog-job-route';

function serviceHref(serviceName?: string): string {
  return `/services/${encodeURIComponent(serviceName || '')}`;
}

function KeyValue({ label, value }: { label: string; value?: string | null }) {
  return (
    <Column spacing="100">
      <Text type="b100" color="secondary">
        {label}
      </Text>
      <Text type="b200" breakWord>
        {value || '-'}
      </Text>
    </Column>
  );
}

function JobStatus() {
  const { serviceName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const catalogJob = useCatalogJob(serviceName || '');
  const [handedOverJob, setHandedOverJob] = useState<SubmittedCatalogJob | null>(null);
  const handedOverFromLocation = (location.state as CatalogJobLocationState | null)?.submittedCatalogJob;

  // The onboarding dialog submits the job and hands its ID over in router state. Clearing that state
  // with a replace keeps a reload of this page from showing a job ID it did not just receive.
  useEffect(() => {
    if (!handedOverFromLocation) return;

    setHandedOverJob(handedOverFromLocation);
    navigate(location.pathname, { replace: true, state: null });
  }, [handedOverFromLocation, location.pathname, navigate]);

  const handedOverJobId =
    handedOverJob && handedOverJob.serviceName === serviceName ? (handedOverJob.jobId ?? null) : null;
  const currentJobId = catalogJob.submittedJobId ?? handedOverJobId;

  const navigateTo = (href: string) => navigate(href);

  return (
    <Column spacing="400" width="100%" maxWidth={960} minWidth={0}>
      <BreadcrumbGroup>
        <Breadcrumb href="/services" onClick={navigateTo}>
          Services
        </Breadcrumb>
        <Breadcrumb href={catalogJobHref(serviceName)}>Job: {serviceName}</Breadcrumb>
      </BreadcrumbGroup>

      <Row alignmentVertical="bottom" widths={['fill', 'fit']} wrap="down" spacing="300">
        <Column spacing="100">
          <Heading level={1} type="h500">
            Catalog job for {serviceName}
          </Heading>
        </Column>
        <ActionButton variant="tertiary" iconTokens={arrowLeftIconTokens} onClick={() => navigate('/services')}>
          Back to services
        </ActionButton>
      </Row>

      {catalogJob.error && (
        <Alert type="error" size="large" title="Job request failed" titleLevel={2}>
          {catalogJob.error}
        </Alert>
      )}

      <Column type="outline" spacingInset="400" spacing="300">
        {catalogJob.catalogStatus ? (
          <>
            <Row alignmentVertical="center" widths={['fit', 'fill']} spacing="300" wrap="down">
              <StatusBadge status={catalogJob.catalogStatus} />
              <Text type="b200" color="secondary" breakWord>
                {describeCatalogStatus(catalogJob.catalogStatus)}
              </Text>
            </Row>

            <Row spacing="500" wrap="down">
              <KeyValue label="Service" value={serviceName} />
              {currentJobId && <KeyValue label="Current job ID" value={currentJobId} />}
            </Row>

            {catalogJob.catalogStatus === CatalogJobStatus.FAILURE && (
              <CatalogJobFailure
                catalogJobErrorMessage={catalogJob.catalogJobErrorMessage}
                isSubmitting={catalogJob.isSubmitting}
                onRetry={catalogJob.submit}
              />
            )}

            <CatalogJobPollingStatus
              isPolling={catalogJob.isPolling}
              pollCount={catalogJob.pollCount}
              pollLimitReached={catalogJob.pollLimitReached}
              isCheckingJobStatus={catalogJob.isSubmitting}
              onRefreshNow={catalogJob.loadStatus}
              showDivider
              onContinue={catalogJob.startPolling}
              onCheckJobStatus={catalogJob.checkJobStatus}
              onStop={catalogJob.dismissPollLimit}
            />

            <Row spacing="300" wrap="down">
              {catalogJob.catalogStatus === CatalogJobStatus.SUCCESS && (
                <ActionButton variant="primary" onClick={() => navigate(serviceHref(serviceName))}>
                  View service
                </ActionButton>
              )}
              {!catalogJob.isPolling &&
                !catalogJob.pollLimitReached &&
                catalogJob.catalogStatus === CatalogJobStatus.PENDING && (
                  <ActionButton variant="secondary" onClick={catalogJob.startPolling}>
                    Resume polling
                  </ActionButton>
                )}
            </Row>
          </>
        ) : catalogJob.isServiceNotCataloged ? (
          <Column spacing="300">
            <Heading level={2} type="h300">
              No catalog job for this service
            </Heading>
            <Text type="b200" color="secondary">
              Submit a catalog job to generate tool schemas for {serviceName}.
            </Text>
            <Row>
              <ActionButton
                variant="primary"
                onClick={catalogJob.submit}
                busy={catalogJob.isSubmitting}
                disabled={catalogJob.isSubmitting}
              >
                {catalogJob.isSubmitting ? 'Submitting...' : 'Submit catalog job'}
              </ActionButton>
            </Row>
          </Column>
        ) : catalogJob.error ? (
          <Column spacing="300">
            <Heading level={2} type="h300">
              Job status unavailable
            </Heading>
            <Text type="b200" color="secondary">
              Review the error above, then try loading the job status again.
            </Text>
            <Row>
              <ActionButton
                variant="primary"
                onClick={catalogJob.loadStatus}
                busy={catalogJob.isLoadingStatus}
                disabled={catalogJob.isLoadingStatus}
              >
                {catalogJob.isLoadingStatus ? 'Loading...' : 'Try again'}
              </ActionButton>
            </Row>
          </Column>
        ) : (
          <Column alignmentHorizontal="center" spacingInset="600" spacing="300">
            <Loader size="large" label="Loading catalog job status" />
            <Text type="b200" color="secondary">
              Loading catalog job status...
            </Text>
          </Column>
        )}
      </Column>
    </Column>
  );
}

export default JobStatus;
